import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import { items } from '@/pages/Patient/components/PatientChart.utils.tsx'
import type { ResourceType } from '@medplum/fhirtypes'
import { patientChartCountsQueryKey } from './patientChartCountsQueryKey'

function getCountableItems() {
  return items.filter((i) => i.resourceTypes[0] && i.relation && !i.hidden)
}

function buildCountQuery(patientId: string, relation: string, extraQuery?: string): string {
  const cleanExtra = extraQuery
    ?.split('&')
    .filter((p) => !p.startsWith('_include') && !p.startsWith('_revinclude'))
    .join('&')
  return `${relation}=Patient/${patientId}${cleanExtra ? `&${cleanExtra}` : ''}&_summary=count`
}

function storageKey(patientId: string): string {
  return `patient-chart-counts:${patientId}`
}

function readFromStorage(patientId: string): Record<string, number> | undefined {
  try {
    const raw = localStorage.getItem(storageKey(patientId))
    return raw ? (JSON.parse(raw) as Record<string, number>) : undefined
  } catch {
    return undefined
  }
}

function writeToStorage(patientId: string, counts: Record<string, number>): void {
  try {
    localStorage.setItem(storageKey(patientId), JSON.stringify(counts))
  } catch {
    // localStorage unavailable or full — silently skip
  }
}

export function usePatientChartCounts(patientId: string | undefined) {
  const medplum = useMedplum()

  const query = useQuery({
    queryKey: patientId
      ? patientChartCountsQueryKey(patientId)
      : ['patient-chart-counts', { patientId: 'none' }],
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    initialData: () => (patientId ? readFromStorage(patientId) : undefined),
    // 0 = always treat stored data as stale so a fresh fetch runs in the background
    initialDataUpdatedAt: 0,
    queryFn: async (): Promise<Record<string, number>> => {
      const results = await Promise.all(
        getCountableItems().map((item) =>
          medplum
            .search(
              item.resourceTypes[0] as ResourceType,
              buildCountQuery(
                patientId!,
                item.relation,
                [item.extraQuery, item.countExtraQuery].filter(Boolean).join('&') || undefined,
              ),
              { cache: 'no-cache' },
            )
            .then((bundle) => ({ dataKey: item.dataKey, count: bundle.total ?? 0 }))
            .catch(() => ({ dataKey: item.dataKey, count: 0 })),
        ),
      )
      return Object.fromEntries(results.map(({ dataKey, count }) => [dataKey, count]))
    },
  })

  useEffect(() => {
    if (patientId && query.data) {
      writeToStorage(patientId, query.data)
    }
  }, [patientId, query.data])

  return query
}
